import React, { useState, useEffect, useRef } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Box } from "../Box/Box";
import messages from "./messages";
import { ResolvedCopy } from "./types";

// ── Tween Hook ────────────────────────────────────────────────────────────────
interface TweenOptions {
  ms?: number;
  startFrom?: number;
  replayToken?: number;
}

export function useTween(target: number, { ms = 900, startFrom, replayToken = 0 }: TweenOptions = {}) {
  const init = startFrom !== undefined ? startFrom : target;
  const [val, setVal] = useState(init);
  const vRef = useRef(init);
  const rafRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const from = startFrom !== undefined ? startFrom : vRef.current;
    vRef.current = from;
    setVal(from);

    const t0 = performance.now();
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - t0) / ms);
      const eased = 1 - Math.pow(1 - elapsed, 3); // cubic ease out
      const v = from + (target - from) * eased;
      vRef.current = v;
      setVal(v);

      if (elapsed < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [target, ms, startFrom, replayToken]);

  return val;
}

// ── Copy Resolution ───────────────────────────────────────────────────────────
const bonusFormatter = (...chunks: React.ReactNode[]) => (
  <Box as="span" color="#FF478F" fontWeight="700">
    {chunks}
  </Box>
);

const boldFormatter = (...chunks: React.ReactNode[]) => (
  <Box as="span" color="gray.800" fontWeight="700">
    {chunks}
  </Box>
);

const gradientFormatter = (...chunks: React.ReactNode[]) => (
  <Box
    as="span"
    fontWeight="700"
    bgGradient="linear(to-r, #FF478F, #FF9800)"
    bgClip="text"
    textColor="transparent"
    className="rt-grad"
  >
    {chunks}
  </Box>
);

const valuesMap = {
  bonus: bonusFormatter,
  bold: boldFormatter,
  gradient: gradientFormatter,
};

export function resolveCopy(score: number, hoveredReward: number | null): [ResolvedCopy, number] {
  if (hoveredReward === 1) {
    return [
      {
        pre: <FormattedMessage {...messages.reach} />,
        heading: null,
        sub: <FormattedMessage {...messages.seeJobs1HourSooner} values={valuesMap} />,
      },
      6,
    ];
  }
  if (hoveredReward === 2) {
    return [
      {
        pre: <FormattedMessage {...messages.reach} />,
        heading: null,
        sub: <FormattedMessage {...messages.seeJobs2HoursSooner} values={valuesMap} />,
      },
      7,
    ];
  }
  if (hoveredReward === 3) {
    return [
      {
        pre: <FormattedMessage {...messages.reach} />,
        heading: null,
        sub: <FormattedMessage {...messages.becomeSuperTalent} values={valuesMap} />,
      },
      8,
    ];
  }

  if (score >= 8) {
    return [
      {
        heading: <FormattedMessage {...messages.default8Heading} values={valuesMap} />,
        sub: <FormattedMessage {...messages.default8Sub} values={valuesMap} />,
      },
      8,
    ];
  }
  if (score >= 7) {
    return [
      {
        heading: <FormattedMessage {...messages.default7Heading} />,
        sub: <FormattedMessage {...messages.default7Sub} values={valuesMap} />,
      },
      score,
    ];
  }
  if (score >= 6) {
    return [
      {
        heading: <FormattedMessage {...messages.default6Heading} />,
        sub: <FormattedMessage {...messages.default6Sub} values={valuesMap} />,
      },
      score,
    ];
  }

  return [
    {
      heading: <FormattedMessage {...messages.defaultPreHeading} />,
      sub: <FormattedMessage {...messages.defaultPreSub} />,
    },
    score,
  ];
}
